import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OrderService } from '../orders/shared/order.service';
import { CartItem } from '../models/cart-item.model';
import { Customer } from '../models/customer.model';
import { User } from '../models/user.model';
import { MessageService } from '../messages/message.service';
import { Router } from '@angular/router';
import { AuthService } from '../account/shared/auth.service';
import { CartService } from '../my-cart/shared/cart.service';
import { CheckoutService } from './shared/checkout.service';

@Component({
  selector: 'my-app-checkout',
  templateUrl: './my-checkout.component.html',
  styleUrls: ['./my-checkout.component.scss']
})
export class MyCheckoutComponent implements OnInit, OnDestroy {
  checkoutSubscription: Subscription;
  steps: string[] = ['Address'];//, 'Shipping', 'Review'
  activeStep: number = 0;
  isMobile: boolean = false;
  unsubscribe$ = new Subject();
  subTotal: number = 0;
  shippingMethod: number = 40; // Default to Standard Shipping
  total: number = 0;
  addressForm: FormGroup;

  items: CartItem[];
  customer: any;
  user: User;

  constructor(
    private cartService: CartService,
    private checkoutService: CheckoutService,
    private orderService: OrderService,
    private router: Router,
    private authService: AuthService,
    private messageService: MessageService,
    private fb: FormBuilder
  ) {
    // Initialize the address form with all Customer properties
    this.addressForm = this.fb.group({
      name: ['', Validators.required],
      phone: ['', [Validators.required, Validators.pattern('[0-9\\s\\-\\+()]*')]],
      address: ['', Validators.required],
      city: ['', Validators.required],
      postalCode: ['', Validators.required],
      country: ['', Validators.required]
    });
  }

  ngOnInit() {
    // Subscribe to cart changes
    this.cartService.itemsChanged.subscribe((items) => {
      this.subTotal = this.cartService.getTotal()*13;
      this.items = items;
      this.total = this.subTotal + this.shippingMethod;
    });
    // Initial load
    this.subTotal = this.cartService.getTotal()*13;
    this.items = this.cartService.getItems();
    this.total = this.subTotal + this.shippingMethod;
    this.activeStep = this.checkoutService.activeStep;

    this.checkoutSubscription = this.checkoutService.stepChanged.subscribe((step: number) => {
      this.activeStep = step;
    });
    this.checkScreenSize();

    // Pre-fill form if user is logged in
    this.authService.user.subscribe((user) => {
      this.user = user;
      if (user) {
        this.addressForm.patchValue({
          firstname: user.firstName || '',
          lastname: user.lastName || '',
          email: user.email || '',
          phone: '',
          address1: '',
          address2: '',
          city: '',
          zip: null,
          country: '',
          company: '',
          note: '',
          location: ''
        });
      }
    });
  }

  updateShippingCost(cost: number) {
    this.shippingMethod = cost;
    this.total = this.subTotal + this.shippingMethod;
  }

  nextStep() {
    if (this.activeStep === 0 && this.addressForm.invalid) {
      this.addressForm.markAllAsTouched();
      this.messageService.addError('Please fill in all required fields.');
      return;
    }
    if (this.activeStep === 0) {
      // Split name into firstname and lastname
      const nameParts = this.addressForm.get('name')?.value.split(' ').filter(part => part);
      const firstname = nameParts[0] || '';
      const lastname = nameParts.slice(1).join(' ') || '';

      // Save customer data from form, mapping to Customer class
      this.customer = new Customer(
        firstname,
        lastname,
        this.addressForm.get('address')?.value,
        '',
        parseInt(this.addressForm.get('postalCode')?.value, 10) || null,
        this.addressForm.get('city')?.value,
        '',
        this.addressForm.get('phone')?.value,
        '',
        this.addressForm.get('country')?.value,
        '',
        ''
      );
    }
    if (this.activeStep < this.steps.length - 1) {
      this.checkoutService.nextStep();
    }
  }

  prevStep() {
    if (this.activeStep > 0) {
      this.checkoutService.prevStep();
    }
  }

  ngOnDestroy() {
    if (this.checkoutSubscription) {
      this.checkoutSubscription.unsubscribe();
    }
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    this.checkScreenSize();
  }

  checkScreenSize() {
    this.isMobile = window.innerWidth <= 768;
  }

  setActiveStep(i: number) {
    if (i <= this.activeStep || (i === this.activeStep + 1 && this.addressForm.valid)) {
      this.checkoutService.gotoStep(i);
    } else if (i === this.activeStep + 1 && this.activeStep === 0) {
      this.addressForm.markAllAsTouched();
      this.messageService.addError('Please fill in all required fields.');
    }
  }

  placeOrder() {
    if (this.activeStep !== this.steps.length - 1) {
      this.messageService.addError('Please complete all steps.');
      return;
    }
    this.nextStep();
    const order = this.checkoutService.getOrderInProgress();
    order.customer = this.customer; // Assign customer data to order
    order.items = this.cartService.getItems();
    order.total = this.total;

    const userUid = this.user ? this.user.uid : false;

    this.checkoutService.setOrderItems(this.cartService.getItems());

  //  if (userUid) {
  //    this.submitUserOrder(order, this.total, userUid);
  //  } else {
      this.submitAnonOrder(order, this.total);
  //  }
  }

  private submitUserOrder(order: any, total: number, userUid: string) {
    this.orderService
      .addUserOrder(order, total, userUid)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          this.cartService.clearCart();
          this.checkoutService.resetSteps();
          this.router.navigate(['/order-complete']);
        },
        (error) => {
          this.messageService.addError('Could not submit order, try again.');
        }
      );
  }

  private submitAnonOrder(order: any, total: number) {
    console.log('Submitting anonymous order:', order, total);
    this.orderService
      .addAnonymousOrder(order, total)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response) => {
          this.cartService.clearCart();
          this.checkoutService.resetSteps();
          this.router.navigate(['/order-complete']);
        },
        (error) => {
          this.messageService.addError('Could not submit order, try again.');
        }
      );
  }
}
